// src/app/containers/Features/containers/TeamSettings/helpers.tsx
import { MySettingsIcon } from "app/components/Icons";
import AddonsIcon from "app/components/Icons/AddonsIcon";
import ArchiveIcon from "app/components/Icons/ArchiveIcon";
import AuditLogsIcon from "app/components/Icons/AuditLogsIcon";
import BillingIcon from "app/components/Icons/BillingIcon";
import ImportExportIcon from "app/components/Icons/ImportExportIcon";
import InfoIcon from "app/components/Icons/InfoIcon";
import MembersIcon from "app/components/Icons/MembersIcon";
import NotificationIcon from "app/components/Icons/NotificationIcon";
import ProjectListIcon from "app/components/Icons/ProjectListIcon";
import { ReferralsIcon } from "app/components/Icons/ReferralsIcon";
import SecurityIcon from "app/components/Icons/SecurityIcon";
import { LogOutIcon } from "lucide-react";
import React from "react";
import { IAvatar } from "./container/MySetting/types";
import googleImg from "assets/imgs/flat-color-icons_google.png";
import slackImg from "assets/imgs/devicon_slack.png";
import microsoftImg from "assets/imgs/logos_microsoft-icon.png";

export const getColorByStatus = (status) => {
  const colorMap = {
    "TO DO": {
      bgColor: "bg-[#73A9F2]",
      textColor: "text-white",
    },
    "IN PROGRESS": {
      bgColor: "bg-[#30D476]",
      textColor: "text-white",
    },
    DONE: {
      bgColor: "bg-[#F2994A]",
      textColor: "text-white",
    },
    REOPENED: {
      bgColor: "bg-[#F28282]",
      textColor: "text-white",
    },
    "IN CODE REVIEW": {
      bgColor: "bg-[#BC86ED]",
      textColor: "text-white",
    },
    ABANDONED: {
      bgColor: "bg-[#20DBDF]",
      textColor: "text-white",
    },
    "IN STAGING": {
      bgColor: "bg-[#F577BE]",
      textColor: "text-white",
    },
    "TESTING IN PRODUCTION": {
      bgColor: "bg-[#CD9950]",
      textColor: "text-white",
    },
    Active: {
      bgColor: "bg-[#219653]",
      textColor: "text-white",
    },
    Inactive: {
      bgColor: "bg-[#79808A]",
      textColor: "text-white",
    },
    "Invitation pending": {
      bgColor: "bg-[#FC9D4A]",
      textColor: "text-white",
    },
    "Invitation rejected": {
      bgColor: "bg-[#FC7373]",
      textColor: "text-white",
    },
    default: {
      bgColor: "bg-[#36B37E]",
      textColor: "text-white",
    },
  };

  return colorMap[status] || colorMap.default;
};

export const Links = [
  {
    name: "General info",
    path: "/app/team/{id}/general-info",
    icon: <InfoIcon />,
  },
  {
    name: "Projects",
    path: "/app/team/{id}/projects",
    icon: <ProjectListIcon />,
    children: [
      {
        name: "details",
        title: "settings",
        path: "/app/team/{id}/projects/{projectId}/details",
      },
      {
        name: "members",
        title: "members",
        path: "/app/team/{id}/projects/{projectId}/members",
      },
      {
        name: "components",
        title: "components",
        path: "/app/team/{id}/projects/{projectId}/components",
      },
      {
        name: "active-workflow",
        title: "workflow",
        path: "/app/team/{id}/projects/{projectId}/active-workflow",
      },
      {
        name: "work-items",
        title: "work items",
        path: "/app/team/{id}/projects/{projectId}/work-items",
      },
      {
        name: "custom-fields",
        title: "custom fields",
        path: "/app/team/{id}/projects/{projectId}/custom-fields",
      },
    ],
  },
  {
    name: "Members",
    path: "/app/team/{id}/members",
    icon: <MembersIcon />,
  },
  {
    name: "Billing",
    path: "/app/team/{id}/billing",
    icon: <BillingIcon />,
  },
  {
    name: "Add-ons",
    path: "/app/team/{id}/addons",
    icon: <AddonsIcon />,
  },
  {
    name: "Import/export",
    path: "/app/team/{id}/import-export",
    icon: <ImportExportIcon />,
    children: [
      {
        name: "import",
        title: "Import",
        path: "/app/team/{id}/import-export/import",
      },
      {
        name: "export",
        title: "Export",
        path: "/app/team/{id}/import-export/export",
      },
    ],
  },
  {
    name: "Security",
    path: "/app/team/{id}/security",
    icon: <SecurityIcon />,
  },
  {
    name: "Audit logs",
    path: "/app/team/{id}/audit-logs",
    icon: <AuditLogsIcon />,
  },
  {
    name: "Archives",
    path: "/app/team/{id}/archives",
    icon: <ArchiveIcon />,
  },
  {
    name: "My settings",
    path: "/app/team/{id}/settings",
    icon: <MySettingsIcon />,
  },
  {
    name: "Notifications",
    path: "/app/team/{id}/notifications",
    icon: <NotificationIcon />,
  },
  {
    name: "Referrals",
    path: "/app/team/{id}/referrals",
    icon: <ReferralsIcon />,
  },
  {
    name: "Log out",
    action: "logout",
    icon: <LogOutIcon />,
  },
];

export function normalizeAvatarUpdate(
  current: IAvatar,
  update: Partial<IAvatar>,
): IAvatar {
  const isFileUpdate = update.file !== undefined || update.fileId !== undefined;

  const isIconOrColorUpdate =
    update.icon !== undefined || update.color !== undefined;

  let result: IAvatar = {
    ...current,
    ...update,
  };

  // If file updated → clear icon/color
  if (isFileUpdate) {
    result.icon = "";
    result.color = "";
  }

  // If icon/color updated → clear file info
  if (isIconOrColorUpdate && !isFileUpdate) {
    result.file = "";
    result.fileId = "";
  }

  return result;
}

export const convertToRGBA = (hex: string, opacity: number) => {
  let c = hex.replace("#", "");

  if (c.length === 3) {
    c = c
      .split("")
      .map((x) => x + x)
      .join("");
  }

  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const getMemberAddFormIcon = (icon: string) => {
  switch (icon) {
    case "googleImg":
      return (
        <img src={googleImg} alt="googleImg" className="size-6 object-fit " />
      );
    case "slackImg":
      return (
        <img src={slackImg} alt="slackImg" className="size-6 object-fit " />
      );
    case "microsoftImg":
      return (
        <img
          src={microsoftImg}
          alt="microsoftImg"
          className="size-6 object-fit "
        />
      );
  }
};
